import AppModal from 'components/common/modal/AppModal';
import PlainModal from 'components/common/modal/PlainModal';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import CreateProject from 'components/project/CreateProject';
import ProjectRemakeModal from 'components/project/ProjectRemakeModal';
import NewPartnerModalContainer from 'containers/partner/NewPartnerModalContainer';
import PatientModalContainer from 'containers/patient/PatientModalContainer';
import { ProjectContext } from 'contexts/project/ProjectContext';
import useImmerInput from 'lib/hooks/useImmerInput';
import useInput from 'lib/hooks/useInput';
import { cutUrl, makeCaseIdFnClinic, makeCaseIdFnLab } from 'lib/library';
import { genderList, mandibleList, maxillaList, pageUrl } from 'lib/mapper';
import { IProjectPatients } from 'lib/types/patientTypes';
import { useDidUpdateEffect } from 'lib/utils';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  AppActions,
  PartnerActions,
  PatientActions,
  ProjectActions,
  UserActions,
  UtilActions,
} from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { isClinicUserSelector } from 'store/modules/user';
import { color } from 'styles/utils';

interface InformationFormInput {
  createType: number | string;
  projectTitle: string;
  clientType: number | string;
  client: number | string;
  patient: number | string;
  dueDate: Date;
  senderMemo: string;
  manager: number | string;
}

function CreateProjectContainer({ patientCodeIdx = null }: { patientCodeIdx: string | null }) {
  // SECTION: state
  const {
    language,
    user: { userGroupIdx: userId, groupCode, company },
    projectInit,
    projectInitSuccess,
    indicationFormat,
    indicationInfo,
    indicationOption,
    indicationFormatSuccess,
    indicationInfoSuccess,
    indicationOptionSuccess,
    projectData,
    fetchProjectSuccess,
    createProjectData,
    createProjectSuccess,
    createProjectError,
    createProjectFailure,
    editProjectData,
    editProjectSuccess,
    editProjectError,
    editProjectFailure,
    projectPartnersData,
    projectPartnersSuccess,
    reworkProjectData,
    reworkProjectSuccess,
    remakeTypeList,
    copyProjectData,
    fetchCopyProjectSuccess,
    partnerIndication: { indicationInfo: partnerIndication },
    partnerIndicationSuccess,
    partnerIndicationFailure,
    projectPatientsData: { list: projectPatientsList, pagingData: patientsPagingData },
    projectPatientsSuccess,
    projectPatientsPending,
    isClinicUser,
    registerNewMenualParter: {
      manualPartnerInfo: { groupCode: newMenualPartnerGroupCode },
    },
    registerNewMenualParterSuccess,
    fetchPartnerDetail: { partnerInfo: partnerDetail },
    fetchPartnerDetailSuccess,
    newPatient: { patientInfo: newPatient, msg },
    newPatientSuccess,
    currPatient,
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    user: state.user.user || {},
    projectInit: state.project.initProject.data,
    projectInitSuccess: state.project.initProject.success,
    indicationFormat: state.util.indicationFormat.data?.indicationFormat,
    indicationFormatSuccess: state.util.indicationFormat.success,
    indicationInfo: state.util.indicationInfo.data?.indicationInfo,
    indicationInfoSuccess: state.util.indicationInfo.success,
    indicationOption: state.util.indicationOption.data,
    indicationOptionSuccess: state.util.indicationOption.success,
    projectData: state.project.project.data,
    fetchProjectSuccess: state.project.project.success,
    createProjectData: state.project.createProject.data,
    createProjectSuccess: state.project.createProject.success,
    createProjectError: state.project.createProject.error,
    createProjectFailure: state.project.createProject.failure,
    editProjectData: state.project.editProject.data,
    editProjectSuccess: state.project.editProject.success,
    editProjectError: state.project.editProject.error,
    editProjectFailure: state.project.editProject.failure,
    projectPartnersData: state.partner.projectPartners.data,
    projectPartnersSuccess: state.partner.projectPartners.success,
    reworkProjectData: state.project.copyProject.data,
    reworkProjectSuccess: state.project.copyProject.success,
    remakeTypeList: state.user.fetchMyRemakeList.data?.list,
    copyProjectData: state.project.copyProject.data,
    fetchCopyProjectSuccess: state.project.copyProject.success,
    partnerIndication: state.project.partnerIndication.data || {},
    partnerIndicationSuccess: state.project.partnerIndication.success,
    partnerIndicationFailure: state.project.partnerIndication.failure,
    projectPatientsData: state.patient.projectPatients.data || {},
    projectPatientsSuccess: state.patient.projectPatients.success,
    projectPatientsPending: state.patient.projectPatients.pending,
    isClinicUser: isClinicUserSelector(state),
    registerNewMenualParter: state.partner.newMenualPartner.data || { manualPartnerInfo: {} },
    registerNewMenualParterSuccess: state.partner.newMenualPartner.success,
    fetchPartnerDetail: state.partner.partnerDetail.data || {},
    fetchPartnerDetailSuccess: state.partner.partnerDetail.success,
    newPatient: state.patient.newPatient.data || { patientInfo: {}, msg: {} },
    newPatientSuccess: state.patient.newPatient.success,
    currPatient: state.patient.currPatient,
  }));
  // SECTION: default variable
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { pcode } = useParams() as { pcode: string };
  const history = useHistory();
  const { pcode: projectCodeParam } = useParams() as { pcode: string };
  const { ipcRenderer } = window;
  // fetch project data
  const projectInfo = projectData?.projectInfo;
  const reworkProjectInfo = copyProjectData?.projectInfo;
  const copyOrder = copyProjectData?.projectCopy?.copyOrder;
  const scanModeList: any[] = indicationInfo?.scanmodeList;
  const { reworkProjectCode, copyProjectCode, copyOptionData } = useContext(ProjectContext);
  const projectPartnersPagingData = projectPartnersData?.pagingData;
  const hasMoreProjectPartners =
    projectPartnersPagingData?.page < projectPartnersPagingData?.totalPage;
  const employeeList = projectInit?.employeeList;

  const pageType = cutUrl(pathname, 1);
  const remakeSwitch = useInput(false);
  const [remake, setRemake] = useState<number[]>([]);
  // const remakeChecked = useCheckSetInput(new Set([]));
  const initPatientCode =
    pageType === 'copy'
      ? reworkProjectInfo?.patientInfo?.patientCodeIdx
      : pageType === 'edit'
      ? projectInfo?.patientInfo?.patientCodeIdx
      : patientCodeIdx;
  // information state
  const {
    control: informationControl,
    handleSubmit: handleSubmitInformation,
    errors: informationErrors,
    setValue: setInformation,
    watch: informationWatch,
    register,
    getValues: getInformation,
    formState,
  } = useForm<InformationFormInput>({
    mode: 'onChange',
    defaultValues: {
      createType: 0,
      clientType: 1,
      patient: initPatientCode,
    },
  });

  // SECTION: variable
  const numbering = useInput(0);
  const teeth = useInput([]);
  const tooth = useInput({
    number: null,
    hasDate: false,
  }); // teeth 단수
  const bridge = useInput([]);
  const copyData = useInput({ number: null, hasData: false });
  const toothSvgRef = useRef<HTMLDivElement | any>(null);
  const projectNameRef = useRef<HTMLDivElement | any>(null);
  const teethContextActions = useImmerInput({
    copy: {
      active: false,
      hidden: false,
    },
    paste: {
      active: false,
      hidden: false,
    },
    delete: {
      active: false,
      hidden: false,
    },
  });
  // tooth design
  const restorationPontic = useInput(0);
  const occlusalSurface = useInput(0);
  const ceramicMetal = useInput(0);
  const materialShade = useInput(0);
  const toothShade = useInput(0);
  const scanMode = useInput(1);
  const [scanModeListCt, setScanModeListCt] = useState<any[]>([]);
  const isMaxilla = useInput(false);
  const isMandible = useInput(false);
  const newDate = useInput(null);
  const clientName = useInput('');
  const clientGroupCode = useInput('');
  const patientName = useInput('');
  const [projectCount, setProjectCount] = useState(0);
  const isMultidie = useInput(0);
  // for projectId
  const setProjectCountValue = useMemo(
    () => projectInitSuccess && setProjectCount(projectInit?.projectCount || 0),
    [!!projectInitSuccess],
  );
  const caseId_lab = useMemo(() => {
    let returnValue: string = makeCaseIdFnLab({
      newDate: newDate.value,
      companyName: clientName.value,

      caseCount: projectCount,
      caseIdValue: informationWatch('projectTitle'),
    })(1);

    return returnValue;
  }, [
    company,
    projectCount,
    informationWatch('projectTitle'),
    copyOrder,
    pageType,
    newDate.value,
    clientName.value,
  ]);

  const caseId_clinic = useMemo(() => {
    let returnValue: string = makeCaseIdFnClinic({
      newDate: newDate.value,
      patientName: patientName.value?.split(',')[0].replace(/ /g, ''),
      groupCode: groupCode,

      caseCount: projectCount,
      caseIdValue: informationWatch('projectTitle'),
    })(1);

    return returnValue;
  }, [
    company,
    projectCount,
    informationWatch('projectTitle'),
    copyOrder,
    pageType,
    newDate.value,
    patientName.value,
  ]);

  const caseId = isClinicUser ? caseId_clinic : caseId_lab;

  // Required Information valid check
  const isValidInformationValue = useMemo(() => {
    if (pageType === 'edit') return true;

    let validListLab = [caseId, informationWatch('client'), informationWatch('projectTitle')];
    let validListClinic = [caseId, informationWatch('patient'), informationWatch('projectTitle')];

    const isValid = isClinicUser
      ? validListClinic.every(item => !!item)
      : validListLab.every(item => !!item);

    return isValid;

    // const validList = [
    //   caseId,
    //   informationWatch('client'),
    //   informationWatch('projectTitle'),
    //   // informationWatch('dueDate'),
    // ];

    // return validList.every(item => !!item);
  }, [caseId, informationWatch, reworkProjectCode.value]);

  const isValidClient = useMemo(() => {
    if (pageType === 'edit') return true;

    return !!informationWatch('client') || !!informationWatch('patient');
  }, [!!informationWatch('client'), !!informationWatch('patient')]);

  // Required Indication valid check
  const isValidIndicationValue = useMemo(() => {
    const isValid = [!!teeth.value?.length].every(item => item === true);
    return isValid;
  }, [teeth]);

  const [projectPartners, setProjectPartners] = useState([]);
  const setProjectPartnersValue = useMemo(
    () =>
      projectPartnersSuccess &&
      setProjectPartners(draft => draft.concat(projectPartnersData?.list)),
    [!!projectPartnersSuccess],
  );
  const [projectId, setProjectId] = useState('');
  const projectPatientKeyword = useInput('');
  const [projectPatients, setProjectPatients] = useState<IProjectPatients[]>([]);
  const setProjectPatientsValue = useMemo(
    () => projectPatientsSuccess && setProjectPatients(draft => draft.concat(projectPatientsList)),
    [!!projectPatientsSuccess],
  );
  const [copyOptions, setCopyOptions] = useState<string[]>([]);
  const [partnerIdx, setPartnerIdx] = useState(null);
  const [prevProjectId, setPrevProjectId] = useState('');
  const scanModeTemp = useInput('');
  const [teethTreatmentIdx, setTeethTreatmentIdx] = useState(null);
  const [projectCode, setProjectCode] = useState('');
  const patientCode = useInput('');

  const isClientError = useInput(false);
  const isPatientError = useInput(false);
  const hasMoreProjectPatients = patientsPagingData?.page < patientsPagingData?.totalPage;

  // modal list
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>('');
  const [isModalClose, setIsModalClose] = useState<boolean>(false);
  const isModalModify = useInput(false);
  const majorClassify = useInput('');
  const minorClassify = useInput('');
  const [prevPartnerStateProp, setPrevPartnerStateProp] = useState<number | null>(null);
  const detailPartnerGroupCode = useInput('');
  const receivedRequestGroupCode = useInput('');
  const updateSucessType = useInput(''); //상태변화 상태 - new, modify(information, profileImage), delete
  const updateUserData = useInput(null); //상태변화 값
  // fetch project state
  const [stage, setStage] = useState(0);
  const [timeline, setTimeline] = useState({ create: moment(moment.now()).unix() });

  const [isProjectCreator, setIsProjectCreator] = useState<boolean>(false);

  const handleClosePartnerModal = () => {
    receivedRequestGroupCode.setValue('');
    setIsModalOpen(false);
    setIsModalClose(false);
    isModalModify.setValue(false);
    majorClassify.setValue('');
    minorClassify.setValue('');
    setPrevPartnerStateProp(null);
  };

  const handleClosePatientModal = () => {
    setIsModalOpen(false);
    setIsModalClose(false);
  };

  const handleCloseRemakeModal = () => {};

  const handleSaveRemake = (selectedList: number[]) => {
    setRemake(selectedList.sort());
    setIsModalOpen(false);
    setIsModalClose(false);
  };

  const modalList = [
    {
      type: 'newPartner',
      title: t('GLOBAL_NEW_PARTNER'),
      component: (
        <NewPartnerModalContainer
          major={majorClassify}
          minor={minorClassify}
          prevPartnerStateProp={prevPartnerStateProp}
          detailPartnerGroupCode={detailPartnerGroupCode}
          receivedRequestGroupCode={receivedRequestGroupCode.value}
          updateSucessType={updateSucessType}
          updateUserData={updateUserData}
          onClosePartnerModal={handleClosePartnerModal}
          isDirectInput
        />
      ),
      width: 'auto',
      padding: 0,
      backgroundColor: 'transparent',
    },
    {
      type: 'newPatient',
      title: t('GLOBAL_NEW_PATIENT'),
      component: <PatientModalContainer type="new" onCloseModal={handleClosePatientModal} />,
      width: 'auto',
      padding: 0,
      backgroundColor: 'transparent',
    },
    {
      type: 'remake',
      title: t('GLOBAL_REMAKE'),
      component: (
        <ProjectRemakeModal
          remakeList={remakeTypeList}
          remake={remake}
          onSaveRemake={handleSaveRemake}
          onClose={handleCloseRemakeModal}
        />
      ),
      width: 'auto',
      padding: 0,
      backgroundColor: 'transparent',
    },
  ];

  useEffect(() => {
    // NOTE: 프로젝트 권한 판별 -> edit인 경우, 권한 체크
    if (pageType !== 'edit') {
      setIsProjectCreator(true);
    } else {
      if (groupCode === projectInfo?.ownerGroupCode) {
        setIsProjectCreator(true);
      } else {
        setIsProjectCreator(false);
      }
    }
  }, [projectInfo, groupCode]);

  useEffect(() => {
    console.log('HDS] isProjectCreator ________ ', isProjectCreator);
  }, [isProjectCreator]);

  useEffect(() => {
    console.log('HDS] projectInfo ________ ', projectInfo);
  }, [projectInfo]);

  // SECTION: declare hooks
  useEffect(() => {
    UtilActions.fetch_teeth_indication_info_request({
      groupCode: !!groupCode ? groupCode : '',
    });

    if (!projectNameRef || pageType === 'edit') return;
    projectNameRef?.current?.focus();
  }, []);

  useDidUpdateEffect(() => {
    if (!!isMaxilla.value && !!isMandible.value) {
      // 상악, 하악 둘다 선택 됨
      setScanModeListCt([...scanModeList]);
    } else {
      // 상악, 하악 둘중에 한곳만 선택된 경우 보여줄 리스트 목록
      const maxillaOrMandible = [1, 14];
      const isMaxillaOrMandible: any[] = scanModeList?.filter((scanMode: any) => {
        return maxillaOrMandible.find(item => item === scanMode.idx);
      });
      setScanModeListCt([...isMaxillaOrMandible]);
    }
  }, [indicationInfoSuccess, isMaxilla.value, isMandible.value]);

  useEffect(() => {
    // Teeth 변경 시, 상악/하악 체크
    if (teeth.value.length > 0) {
      const maxiilla = teeth.value.filter((item: any) => maxillaList.includes(item.number));
      const mandible = teeth.value.filter((item: any) => mandibleList.includes(item.number));
      if (maxiilla.length > 0) {
        isMaxilla.setValue(true);
      } else {
        isMaxilla.setValue(false);
      }
      if (mandible.length > 0) {
        isMandible.setValue(true);
      } else {
        isMandible.setValue(false);
      }
    }
  }, [teeth.value]);

  useEffect(() => {
    // 수정 또는 복제로 들어온 경우, scanmode 설정
    if (!scanModeTemp.value) return;
    scanMode.setValue(scanModeTemp.value);
    scanModeTemp.setValue('');
  }, [scanModeListCt, indicationInfoSuccess]);

  useEffect(() => {
    // 신규 생성 또는 수정 또는 복제에서 치아 변경시 , scanmode 설정
    if (!!scanModeTemp.value) return;
    if (!!isMaxilla.value && !!isMandible.value) {
      scanMode.setValue(2);
    } else {
      scanMode.setValue(1);
    }
  }, [scanModeListCt, isMaxilla.value, isMandible.value]);

  // change createType, intialize data
  useDidUpdateEffect(() => {
    if (informationWatch('createType') === 0) {
      ProjectActions.fetch_rework_project_clear();

      setInformation('client', '0');
      teeth.setValue([]);
    }
  }, [informationWatch('createType')]);

  // change partnerType
  useDidUpdateEffect(() => {
    setProjectPartners([]);
    PartnerActions.fetch_project_partners_request({
      userTypeIdx: informationWatch('clientType') === 1 ? `1` : `2%3`,
      page: 1,
    });
  }, [informationWatch('clientType')]);

  useDidUpdateEffect(() => {
    if (!!projectPartnersData && !!informationWatch('client')) {
      const userGroupCode = projectPartnersData.list.find(
        (item: any) => item?.userGroupIdx === informationWatch('client'),
      )?.userGroupCode;
      clientGroupCode.setValue(userGroupCode);
    }
  }, [informationWatch('client'), projectPartnersData]);

  // when edit, fetch project
  useDidUpdateEffect(() => {
    if (!fetchProjectSuccess && !!setInformation) return;
    // timeline
    setStage(projectInfo?.stage || 0);
    setTimeline(projectInfo?.timeline || {});
    setProjectCount(projectInfo?.applyCount || 0);
  }, [!!fetchProjectSuccess]);

  // when edit and rework, fetch project
  useDidUpdateEffect(() => {
    if (!(fetchProjectSuccess || fetchCopyProjectSuccess)) return;
    let projectInfoData = null;
    if (fetchProjectSuccess) projectInfoData = projectInfo;
    if (fetchCopyProjectSuccess) projectInfoData = reworkProjectInfo;

    let projectIndicationData = projectInfoData?.indication;
    const remakeData = projectInfoData.ownerRemakeType?.reduce(
      (acc: any[], curr: any, index: number) => {
        return acc.concat(curr.remakeTypeIdx);
      },
      [],
    );
    remakeSwitch.setValue(!!remakeData.length);
    // remakeChecked.setValue(new Set([...remakeData]));
    setRemake([...remakeData]);
    setCopyOptions([...copyOptionData.value]);
    setPartnerIdx(projectInfoData.partnerIdx || null);
    setProjectId(!!fetchCopyProjectSuccess ? null : projectInfoData.projectId || null);
    setPrevProjectId(projectInfoData.projectId || null);
    toothShade.setValue(projectIndicationData?.toothShade || 0);
    numbering.setValue(projectIndicationData?.notation || 0);
    teeth.setValue(projectIndicationData?.teeth || []);
    bridge.setValue(projectIndicationData?.bridgeList || []);
    restorationPontic.setValue(projectIndicationData?.restorationPonticDesign);
    occlusalSurface.setValue(projectIndicationData?.occlusalSurfaceDesign);
    ceramicMetal.setValue(projectIndicationData?.ceramicMetalDesign);
    scanModeTemp.setValue(projectIndicationData?.scanmode.idx);
    isMultidie.setValue(projectInfoData?.isMultidie);
    // edit api data
    setTeethTreatmentIdx(projectIndicationData?.teethTreatmentIdx);
    setProjectCode(projectInfoData?.projectCode);
  }, [!!fetchProjectSuccess, !!reworkProjectSuccess]);

  useDidUpdateEffect(() => {
    if (editProjectSuccess) {
      if (!editProjectData?.projectInformation?.projectCode) return;
      const projectCodeValue = editProjectData?.projectInformation?.projectCode;
      if (!!copyOptions.find(i => i === 'FILE')) {
        if (!!ipcRenderer) {
          handleProjectFileCopy(projectCodeValue);
        }
      } else {
        history.replace(`/project/detail/${projectCodeValue}`, {
          isFromEditComponent: true,
        });
      }
    }
  }, [editProjectSuccess === true]);

  useDidUpdateEffect(() => {
    if (createProjectSuccess) {
      if (!createProjectData?.projectInformation?.projectCode) return;
      const projectCodeValue = createProjectData?.projectInformation?.projectCode;
      if (!!copyOptions.find(i => i === 'FILE')) {
        if (!!ipcRenderer) {
          handleProjectFileCopy(projectCodeValue);
        }
      } else {
        history.replace(`/project/detail/${projectCodeValue}`, {
          isFromCreateComponent: true,
        });
      }
    }
  }, [createProjectSuccess === true]);

  useDidUpdateEffect(() => {
    {
      ProjectActions.init_project_request({ newDate: newDate.value });
    }
  }, [newDate.value]);

  useEffect(() => {
    UtilActions.fetch_teeth_indication_info_request({
      groupCode: !!groupCode ? groupCode : '',
    });
    UserActions.fetch_my_remake_list_request();
    ProjectActions.init_project_request();
    PartnerActions.fetch_project_partners_request({
      userTypeIdx: informationWatch('clientType') === 1 ? `1` : `2%3`,
      page: 1,
    });
    if (pageType === 'edit') {
      // NOTE: props or Context value 에서 reject 또는 done의 경우 ex. fromStage
      ProjectActions.fetch_project_request(projectCodeParam);
    }

    if (pageType === 'copy') {
      ProjectActions.fetch_copy_project_request(projectCodeParam);
    }

    return () => {
      copyOptionData.setValue([]);
      ProjectActions.fetch_copy_project_clear();
      ProjectActions.fetch_project_clear();
    };
  }, []);

  useEffect(() => {
    UtilActions.fetch_teeth_indication_format_request({
      groupCode: !!groupCode ? groupCode : '',
      isMultidie: isMultidie.value,
    });
    if (pageType === 'create') {
      handleInitIndication();
    }
  }, [isMultidie.value]);

  useEffect(() => {
    // PartnerActions.fetch_sync_partners_request({ keyword: '', page: 1 });
    PatientActions.fetch_project_patients_request({ keyword: '', page: 1 });
  }, []);

  useDidUpdateEffect(() => {
    if (registerNewMenualParterSuccess) {
      PartnerActions.fetch_partner_detail_request(newMenualPartnerGroupCode);
    }
  }, [registerNewMenualParterSuccess]);

  useDidUpdateEffect(() => {
    if (fetchPartnerDetailSuccess) {
      clientName.setValue(partnerDetail?.name);
      clientGroupCode.setValue(partnerDetail?.userGroupCode);
      setInformation('client', partnerDetail?.userGroupIdx);
    }
  }, [fetchPartnerDetailSuccess]);

  useDidUpdateEffect(() => {
    if (newPatientSuccess) {
      patientName.setValue(
        `${!!newPatient?.firstName ? newPatient.firstName : ''} ${
          !!newPatient?.lastName ? newPatient.lastName : ''
        }${!!newPatient?.identification ? newPatient.identification : ''}, ${
          genderList[newPatient?.gender as keyof typeof genderList]
        }${moment(newPatient.birthday).isValid() ? `, ${newPatient?.birthday}` : ''}`,
      );
      patientCode.setValue(newPatient?.patientCodeIdx);
      setInformation('patient', newPatient?.patientCodeIdx);
    }
  }, [newPatientSuccess]);

  useDidUpdateEffect(() => {
    if (partnerIndicationFailure && !isClinicUser) {
      setProjectPartners([]);
      PartnerActions.fetch_project_partners_request({
        userTypeIdx: informationWatch('clientType') === 1 ? `1` : `2%3`,
        page: 1,
      });
      setInformation('client', null);
      clientName.setValue('');
      clientGroupCode.setValue('');
      patientName.setValue('');
      isClientError.setValue(true);
      isPatientError.setValue(true);
      toothShade.setValue(0);
      numbering.setValue(0);
      teeth.setValue([]);
      bridge.setValue([]);
      restorationPontic.setValue(0);
      occlusalSurface.setValue(0);
      ceramicMetal.setValue(0);
      scanModeTemp.setValue('');
      numbering.setValue(0);
      tooth.setValue({
        number: null,
        hasDate: false,
      });
      AppActions.add_popup({
        isOpen: true,
        timeout: 0,
        title: <T>GLOBAL_ALERT</T>,
        content: <T>NOT_FOUND_PARTNER</T>,
        isCloseIcon: true,
        onClick: () => {},
      });
    }
  }, [partnerIndicationFailure]);

  useDidUpdateEffect(() => {
    if (editProjectFailure) {
      if (editProjectError.message === 'NOT_ENOUGH_POINT') {
        AppActions.add_popup({
          isOpen: true,
          timeout: 0,
          title: <T>GLOBAL_ALERT</T>,
          content: <T>MODAL_NOT_ENOUGH_POINT</T>,
          isCloseIcon: true,
          onClick: () => history.push(pageUrl.store.index),
        });
      }
      if (editProjectError.message === 'NOT_FOUND_PARTNER') {
        AppActions.add_popup({
          isOpen: true,
          timeout: 0,
          title: <T>GLOBAL_ALERT</T>,
          content: <T>NOT_FOUND_PARTNER</T>,
          isCloseIcon: true,
          onClick: () => {},
        });
        setInformation('client', null);
        setProjectPartners([]);
        PartnerActions.fetch_project_partners_request({
          userTypeIdx: informationWatch('clientType') === 1 ? `1` : `2%3`,
          page: 1,
        });
        isClientError.setValue(true);
        isPatientError.setValue(true);
        teeth.setValue([]);
        toothShade.setValue(0);
        bridge.setValue([]);
        restorationPontic.setValue(0);
        occlusalSurface.setValue(0);
        ceramicMetal.setValue(0);
        scanModeTemp.setValue('');
        numbering.setValue(0);
        tooth.setValue({
          number: null,
          hasDate: false,
        });
      }
    }
  }, [editProjectFailure]);

  useEffect(() => {
    let projectInfoData = null;
    if (pageType === 'edit') {
      projectInfoData = projectInfo;
    }
    if (pageType === 'copy') {
      projectInfoData = reworkProjectInfo;
    }
    if (!(pageType === 'edit' || pageType === 'copy')) return;
    setInformation('projectTitle', projectInfoData?.projectName?.substr(0, 20) || '');
    // setProjectNameLength(
    //   !!projectInfoData?.projectName ? informationWatch('projectTitle').length : 0,
    // );
    setInformation(
      'dueDate',
      projectInfoData?.dueDate ? moment.unix(projectInfoData?.dueDate) : null,
    );
    setInformation('senderMemo', projectInfoData?.senderMemo || '');
    // setMemoLength(!!projectInfoData?.senderMemo ? informationWatch('senderMemo').length : 0);
    setInformation('client', projectInfoData?.clientIdx || '');
    setInformation('clientType', Number(projectInfoData?.clientPartnerUserTypeIdx) === 1 ? 1 : 2);
    setInformation(
      'manager',
      typeof projectInfoData?.managerIdx === 'number' ? projectInfoData?.managerIdx : '',
    );
    clientName.setValue(projectInfoData?.clientName);

    if (!projectInfoData) return;

    const { patientInfo } = projectInfoData;

    patientName.setValue(patientInfo?.listText);
    patientCode.setValue(patientInfo?.patientCodeIdx);
  }, [pcode, copyProjectCode.value, reworkProjectInfo, projectInfo]);

  useEffect(() => {
    return () => {
      ProjectActions.fetch_rework_project_clear();
      ProjectActions.fetch_project_clear();
    };
  }, []);

  useEffect(() => {
    if (isClientError.value && !!clientName.value) {
      isClientError.setValue(false);
    }
  }, [clientName.value, isClientError.value]);

  useEffect(() => {
    if (isPatientError.value && !!patientName.value) {
      isPatientError.setValue(false);
    }
  }, [patientName.value, isPatientError.value]);

  useEffect(() => {
    if (!!currPatient) {
      patientName.setValue(currPatient?.patientName);
      patientCode.setValue(currPatient?.patientCodeIdx);
    }

    PatientActions.set_current_patient(null);
  }, [currPatient]);

  // SECTION: declare function
  const handleSearchProjectPatients = debounce((value: string) => {
    // console.log('projectPartnerKeyword.value', projectPartnerKeyword.value);
    projectPatientKeyword.setValue(value);
    // setSyncPatients([]);
    setProjectPatients([]);
    // PartnerActions.fetch_sync_partners_request({ keyword: value || '', page: 1 });
    PatientActions.fetch_project_patients_request({ keyword: value || '', page: 1 });
  }, 500);

  useEffect(() => {
    if (!!currPatient) {
      patientName.setValue(currPatient?.patientName);
      patientCode.setValue(currPatient?.patientCodeIdx);
    }

    PatientActions.set_current_patient(null);
  }, [currPatient]);

  const handleFetchProjectPartners = debounce((first?: boolean) => {
    const { page, totalPage } = projectPartnersPagingData;
    if (page >= totalPage) return;
    PartnerActions.fetch_project_partners_request({
      userTypeIdx: informationWatch('clientType') === 1 ? `1` : `2%3`,
      page: page + 1,
    });
  }, 500);

  // const handleChangeRemake = (index: number) => {
  //   remakeChecked.onChange({ value: index });
  // };

  // const handleCheckRemakeSwitch = (onSwitch: boolean) => {
  //   remakeSwitch.setValue(onSwitch);
  //   if (!onSwitch) {
  //     remakeChecked.setValue(new Set([]));
  //   }
  // };

  const handleCheckCloseModal = () => {
    if (!isModalModify.value) {
      handleClosePartnerModal();
    } else {
      setIsModalClose(true);
      setIsModalOpen(true);
    }
  };

  const handleOpenModalPopup = (type: string | any) => {
    // type :: 0 => UnitPrice , 1 => Employee, 2 => Product
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleClickPartnerModal = () => {
    handleOpenModalPopup('newPartner');
    detailPartnerGroupCode.setValue('');
    majorClassify.setValue('input');
    minorClassify.setValue('new');
  };

  const handleClickPatientModal = () => {
    handleOpenModalPopup('newPatient');
  };

  const handleClickRemakeModal = () => {
    handleOpenModalPopup('remake');
  };

  const handleFetchProjectPatients = debounce((first?: boolean) => {
    // if (first) return PartnerActions.fetch_sync_Patients_request({ keyword: '', page: 1 });
    if (first) return PatientActions.fetch_project_patients_request({ keyword: '', page: 1 });

    const { page, totalPage } = patientsPagingData;
    if (page >= totalPage) return;
    PatientActions.fetch_project_patients_request({
      keyword: projectPatientKeyword.value || '',
      page: page + 1,
    });
    // PartnerActions.fetch_sync_partners_request({
    //   keyword: projectPartnerKeyword.value || '',
    //   page: page + 1,
    // });
  }, 500);

  const handleClickIndication = useCallback(
    (alias: string) => {
      const submitData = {
        client: informationWatch('client'),
        alias,
      };
      ProjectActions.fetch_partner_indication_request(submitData);
    },
    [informationWatch('client')],
  );

  const handleAgreement = (submitData: any) => {
    AppActions.add_popup({
      isOpen: true,
      timeout: 0,
      isCloseIcon: true,
      title: t('PROJECT_NOTI_AGREEMENT'),
      content: (
        <div style={{ textAlign: 'center' }}>
          <CustomText fontSize={21}>{/* <T>AGREEMENT_PARTICIPATION</T> */}</CustomText>
          <CustomText fontSize={15} marginTop={20}>
            <T>AGREEMENT_PARTICIPATION_DETAIL</T>
          </CustomText>
          {/* <CustomText fontSize={13} fontColor={color.blue} marginTop={10}>
            <span style={{ textDecoration: 'underline' }}>
              <a
                href={window.location.protocol + '//' + window.location.host + '/legal/privacy'}
                target="_blank"
              >
                <T>LEGAL_PRIVACY_POLICY</T>
              </a>
            </span>{' '}
            |{' '}
            <span style={{ textDecoration: 'underline' }}>
              <a
                href={window.location.protocol + '//' + window.location.host + '/legal/terms'}
                target="_blank"
              >
                <T>LEGAL_TERMS_OF_SERVICE</T>
              </a>
            </span>
          </CustomText> */}
        </div>
      ),
      onClick: () => {
        if (pageType === 'edit') {
          ProjectActions.edit_project_request({ projectCode, data: { ...submitData } });
        } else {
          ProjectActions.create_project_request(submitData);
        }
      },
    });
  };

  const handleCreateProject = () => {
    // 유효성 체크, Infomation, Indication
    // const isValid = [isValidInformationValue, isValidIndicationValue].every(item => !!item);
    if (!isValidInformationValue) return;
    let teethValidFlag = null;
    let teethValidObjFlag = {};
    teeth.value.map((i: any) => {
      if (!i.indicationIdx || !i.material) {
        teethValidFlag = i.number;
        teethValidObjFlag = { ...i, hasData: true };
      }
    });
    if (!!teethValidFlag) {
      // handleToggleTooth(teethValidFlag);
      tooth.setValue(teethValidObjFlag);
      toothSvgRef.current.focus();
      return;
    }

    const { createType, projectTitle, client, dueDate, senderMemo, manager } = getInformation();

    // request api
    let submitData: any = {
      exocadVersion: 3,
      userIdx: userId,
      projectId: !projectId ? caseId : projectId,
      projectName: projectTitle,
      dueDate: moment.isMoment(dueDate) ? dueDate.unix() : null,
      senderMemo: senderMemo,
      newDate: newDate.value,
      patientCodeIdx: informationWatch('patient') || patientCode.value,
      isMultidie: isMultidie.value,

      participant: {
        client: !isClinicUser ? client : userId,
        manager,
        userIdx: userId,
        partner: partnerIdx,
      },
      copyProjectCode: pageType === 'copy' ? projectCodeParam : '',
      copyProjectOption: [...copyOptions],
      // remakeType: [...remakeChecked.value],
      remakeType: [...remake],
      indication: {
        teeth: teeth.value,
        bridge: bridge.value,
        toothShade: toothShade.value,
        scanmodeIdx: scanMode.value,
        notation: numbering.value,
        restorationPonticDesign: restorationPontic.value,
        ceramicMetalDesign: ceramicMetal.value,
        occlusalSurfaceDesign: occlusalSurface.value,
      },
    };

    if (createType === 1) {
      submitData = {
        ...submitData,
        reworkProjectCode: reworkProjectCode.value,
      };
    }

    if (pageType === 'edit') {
      submitData = {
        ...submitData,
        projectCode,
        teethTreatmentIdx,
      };
    }

    handleAgreement(submitData);
  };

  const handleProjectFileCopy = useCallback(
    async (pcode: string) => {
      const result = await ipcRenderer.invoke('projectCopyAsync', {
        prevProjectId: prevProjectId,
        newProjectId: caseId,
      });
      // if (result.status) history.push(`/project/detail/${pcode}`);
      if (result.status) history.replace(`/project/detail/${pcode}`);
    },

    [prevProjectId, caseId],
  );

  const handleGoBack = () => {
    history.goBack();
  };

  const handleInitIndication = () => {
    teeth.setValue([]);
    bridge.setValue([]);
    toothShade.setValue(0);
    scanMode.setValue(1);
    numbering.setValue(0);
    restorationPontic.setValue(0);
    ceramicMetal.setValue(0);
    occlusalSurface.setValue(0);
  };

  return (
    <>
      {useMemo(() => {
        const findData = modalList.find(item => item.type === modalType);
        return (
          <PlainModal isOpen={isModalOpen} onClose={handleCheckCloseModal} width={findData?.width}>
            <AppModal
              title={findData?.title}
              content={findData?.component}
              // headerCenterText={findData?.headerCenterText?.text}
              // headerCenterTextStyle={{ width: findData?.headerCenterText?.width }}
              contentCardStyle={{
                padding: findData?.padding,
                // borderRadius: findData?.borderRadius,
                backgroundColor: findData?.backgroundColor,
              }}
              isCloseIcon={true}
              onClick={handleCheckCloseModal}
              onCancel={handleCheckCloseModal}
              hideButton={true}
            />
          </PlainModal>
        );
      }, [isModalOpen, modalType, isModalClose, isModalModify.value, remake])}
      <CreateProject
        language={language}
        numbering={numbering}
        teeth={teeth}
        tooth={tooth}
        bridge={bridge}
        copyData={copyData}
        toothSvgRef={toothSvgRef}
        projectNameRef={projectNameRef}
        teethContextActions={teethContextActions}
        toothShade={toothShade}
        indicationFormat={indicationFormat}
        indicationInfo={indicationInfo}
        indicationOption={indicationOption}
        scanMode={scanMode}
        scanModeListCt={scanModeListCt}
        remakeSwitch={remakeSwitch}
        remakeTypeList={remakeTypeList}
        remake={remake}
        restorationPontic={restorationPontic}
        occlusalSurface={occlusalSurface}
        ceramicMetal={ceramicMetal}
        isClinicUser={isClinicUser}
        pageType={pageType}
        isClientError={isClientError}
        isPatientError={isPatientError}
        clientName={clientName}
        clientGroupCode={clientGroupCode}
        patientName={patientName}
        patientCode={patientCode}
        projectPatients={projectPatients}
        isValidInformationValue={isValidInformationValue}
        isValidIndicationValue={isValidIndicationValue}
        isValidClient={isValidClient}
        hasMoreProjectPatients={hasMoreProjectPatients}
        projectPatientsPending={projectPatientsPending}
        isMultidie={isMultidie}
        onFetchProjectPatients={handleFetchProjectPatients}
        onSearchProjectPatients={handleSearchProjectPatients}
        onClickIndication={handleClickIndication}
        // information
        projectPartners={projectPartners}
        hasMoreProjectPartners={hasMoreProjectPartners}
        employeeList={employeeList}
        caseId={!projectId ? caseId : projectId}
        informationControl={informationControl}
        informationErrors={informationErrors}
        setInformation={setInformation}
        informationWatch={informationWatch}
        isExternalSelected={isClinicUser ? newPatientSuccess : fetchPartnerDetailSuccess}
        onCreateProject={handleCreateProject}
        onFetchProjectPartners={handleFetchProjectPartners}
        onClickPartnerModal={handleClickPartnerModal}
        onClickPatientModal={handleClickPatientModal}
        onClickRemakeModal={handleClickRemakeModal}
        onGoBack={handleGoBack}
        isProjectCreator={isProjectCreator}
      />
    </>
  );
}

export default CreateProjectContainer;
